import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Plus, 
  Edit, 
  Trash2, 
  Send, 
  Clock,
  Eye,
  FileText,
  Users,
  Calendar,
  Save,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/api';
import NewsletterContentEditor from './NewsletterContentEditor';

const NewsletterCampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    content: '',
    author: '',
    targetAudience: 'all',
    template: 'default',
    scheduledDate: ''
  });
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/newsletter/campaigns');
      const data = await response.json();
      
      if (response.ok) {
        setCampaigns(data.campaigns || []);
      } else {
        toast.error(data.message || 'Failed to fetch campaigns');
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subject: '',
      content: '',
      author: '',
      targetAudience: 'all',
      template: 'default',
      scheduledDate: ''
    });
    setEditingCampaign(null);
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.subject || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await api.post('/newsletter/campaigns', {
        title: formData.title,
        subject: formData.subject,
        content: formData.content,
        author: formData.author,
        targetAudience: formData.targetAudience,
        template: formData.template,
        scheduledDate: formData.scheduledDate || null
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Campaign created successfully');
        setShowCreateForm(false);
        resetForm();
        fetchCampaigns();
      } else {
        toast.error(data.message || 'Failed to create campaign');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    }
  };

  const handleUpdateCampaign = async (e) => {
    e.preventDefault();
    
    if (!editingCampaign) return;

    try {
      const response = await api.put(`/newsletter/campaigns/${editingCampaign.campaignId}`, {
        title: formData.title,
        subject: formData.subject,
        content: formData.content,
        author: formData.author,
        targetAudience: formData.targetAudience,
        template: formData.template,
        scheduledDate: formData.scheduledDate || null
              });

      const data = await response.json();

      if (response.ok) {
        toast.success('Campaign updated successfully');
        setShowCreateForm(false);
        resetForm();
        fetchCampaigns();
      } else {
        toast.error(data.message || 'Failed to update campaign');
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Failed to update campaign');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      title: campaign.title,
      subject: campaign.subject,
      content: campaign.content,
      author: campaign.author,
      targetAudience: campaign.targetAudience,
      template: campaign.template,
      scheduledDate: campaign.scheduledDate || ''
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      const response = await api.delete(`/newsletter/campaigns/${campaignId}`);

      const data = await response.json();

      if (response.ok) {
        toast.success('Campaign deleted successfully');
        fetchCampaigns();
      } else {
        toast.error(data.message || 'Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handleSchedule = async (campaignId) => {
    const scheduledDate = prompt('Enter scheduled date (YYYY-MM-DD HH:MM):');
    if (!scheduledDate) return;

    try {
      const response = await api.post(`/newsletter/campaigns/${campaignId}/schedule`, {
        scheduledDate: scheduledDate
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Campaign scheduled successfully');
        fetchCampaigns();
      } else {
        toast.error(data.message || 'Failed to schedule campaign');
      }
    } catch (error) {
      console.error('Error scheduling campaign:', error);
      toast.error('Failed to schedule campaign');
    }
  };

  const handleSend = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this campaign to all subscribers?')) {
      return;
    }

    try {
      const response = await api.post(`/newsletter/campaigns/${campaignId}/send`);

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Campaign sent successfully to subscribers');
        fetchCampaigns();
      } else {
        toast.error(data.message || 'Failed to send campaign');
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error('Failed to send campaign');
    }
  };

  const handleSendTestEmail = async (campaignId) => {
    if (!testEmailAddress) {
      toast.error('Please enter a test email address');
      return;
    }

    setSendingTest(true);
    try {
      const response = await api.post(`/newsletter/campaigns/${campaignId}/test`, {
        testEmail: testEmailAddress
      });
      const data = await response.json();

      if (response.ok) {
        toast.success(`Test email sent to ${testEmailAddress}`);
        setTestEmailAddress('');
      } else {
        toast.error(data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'sent': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <FileText className="w-4 h-4" />;
      case 'scheduled': return <Clock className="w-4 h-4" />;
      case 'sent': return <CheckCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Newsletter Campaigns</h2>
          <p className="text-gray-600">Create and manage newsletter content</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateForm(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create Campaign</span>
        </button>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
            </h3>
            <button
              onClick={() => {
                setShowCreateForm(false);
                resetForm();
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={editingCampaign ? handleUpdateCampaign : handleCreateCampaign} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter campaign title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter email subject"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Author
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter author name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Audience
                </label>
                <select
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Subscribers</option>
                  <option value="events">Event Updates</option>
                  <option value="stories">Story Updates</option>
                  <option value="volunteerOpportunities">Volunteer Opportunities</option>
                  <option value="donationUpdates">Donation Updates</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  name="template"
                  value={formData.template}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="default">Default Template</option>
                  <option value="events">Events Template</option>
                  <option value="stories">Stories Template</option>
                  <option value="newsletter">Newsletter Template</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  name="scheduledDate"
                  value={formData.scheduledDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Newsletter Content *
              </label>
              <NewsletterContentEditor
                value={formData.content}
                onChange={handleInputChange}
                placeholder="Create your newsletter content using the rich editor above, or choose from pre-built templates."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>{editingCampaign ? 'Update Campaign' : 'Create Campaign'}</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaigns.map((campaign) => (
                <tr key={campaign.campaignId} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{campaign.title}</div>
                      <div className="text-sm text-gray-500">{campaign.subject}</div>
                      <div className="text-xs text-gray-400">by {campaign.author}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(campaign.status)}`}>
                      {getStatusIcon(campaign.status)}
                      <span className="ml-1">{campaign.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">
                      {campaign.targetAudience.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(campaign.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(campaign)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      
                      {campaign.status === 'draft' && (
                        <button
                          onClick={() => handleSchedule(campaign.campaignId)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Schedule"
                        >
                          <Clock className="w-4 h-4" />
                        </button>
                      )}
                      
                      {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
                        <button
                          onClick={() => handleSend(campaign.campaignId)}
                          className="text-green-600 hover:text-green-900"
                          title="Send to All Subscribers"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => {
                          const email = prompt('Enter test email address:');
                          if (email) {
                            setTestEmailAddress(email);
                            handleSendTestEmail(campaign.campaignId);
                          }
                        }}
                        className="text-purple-600 hover:text-purple-900"
                        title="Send Test Email"
                        disabled={sendingTest}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDelete(campaign.campaignId)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {campaigns.length === 0 && (
          <div className="text-center py-8">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first newsletter campaign.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterCampaignManager; 